<template>
  <b-row class="base-row-padded">
    <b-col>
      <h2>
        {{ pageTitle }}
        <span v-if="orientation"> - {{ orientation ? 'Portrait' : 'Landscape' }}</span>
      </h2>

      <PlaylistPagination :apiUrl="playlistsApiURL" :perPage="20"
        :queryStructure="queryStructure" @items-updated="itemsUpdated"
        ref="paginationComponent" />
      <b-row>
        <b-col xs="10" class="mx-1 mb-2" align="start">
          <b-form inline>
            <b-form-select v-model="queryStructure.custom.status" @change="statusChanged">
              <option :value="null">Filter by publish status</option>
              <option v-for="(value, $index) in publishStatus" :value="$index" :key="$index">{{ value }}</option>
            </b-form-select>
          </b-form>
        </b-col>
        <b-col xs="2" class="mx-1 mb-2" align="end" v-if="!pickerMode">
          <b-button variant="light" @click="openPlaylist" class="mr-2"
            v-if="permission('playlist-change') && playlistsArray && playlistsArray.length > 0">Edit
            Playlists</b-button>
          <b-button variant="success" @click="openCreatePlaylist" v-if="permission('playlist-change')">New
            Playlist</b-button>
        </b-col>
      </b-row>

      <b-row>
        <b-table :fields="fields" :items="playlistsArray" :busy="playlistsArray === null" selectable
          @row-clicked="showPlaylist" :select-mode="'single'" :striped="true" :small="true" :hover="true"
          :responsive="true" :stacked="'sm'">
          <template slot="name" slot-scope="data">
            {{ data.item.name }}
          </template>

          <template #cell(orientation)="data">
            {{ data.item.orientation ? 'Portrait' : 'Landscape' }}
          </template>

          <template #cell(status)="data">
            {{ data.item.status ? 'Published' : 'Unpublished' }}
          </template>
          
          <template #cell(used_in_count)="data">
             <b-button class="m-1" variant="primary" size="sm" v-if="(data.item.used_in_count ?? 0) > 0" @click.prevent="showPlaylistUsageModal(data.item.id)">Used in {{ data.item.used_in_count }}</b-button>
             <p v-else>Unused</p>
          </template>

          <template #cell(id)="data" v-if="!pickerMode">
            <b-button class="m-1" variant="warning" size="sm" v-if="permission('playlist-change')"
              @click.prevent="duplicatePlaylistProxy(data.item.id)">Duplicate</b-button>
            <b-button v-if="permission('publish')" class="m-1" :variant="!data.item.status ? 'success' : 'danger'"
              size="sm" @click.prevent="togglePublish(data.item)">
              {{ !data.item.status ? 'Publish' : 'Unpublish' }}
            </b-button>
            <b-button class="m-1" variant="primary" size="sm" v-if="permission('playlist-change')"
              @click.prevent="showPlaylist(data.item.id)">Edit</b-button>
            <b-button class="m-1" variant="danger" size="sm" v-if="permission('playlist-change')"
              @click.prevent="deletePlaylistProxy(data.item)">Delete</b-button>
          </template>
        </b-table>
      </b-row>
    </b-col>
  </b-row>
</template>

<script>
import formsMixin from '@/mixins/forms';
import playlistsMixin from '@/views/admin/playlists/playlistsMixin';
import rolesMixin from '@/modules/rolesMixin';
import { playlistViewerTitles } from '@/types/playlistTypes';
import PlaylistPagination from './pagination/PlaylistPagination.vue';

export default {
  components: { PlaylistPagination },
  mixins: [formsMixin, playlistsMixin, rolesMixin],
  props: {
    pickerMode: {
      type: [Boolean],
      required: false,
      default: false,
    },
    playlistType: {
      type: [String],
      required: false,
      default: 'playlists',
      validator(v) {
        return ['playlists', 'loop-playlists', 'weekly-playlists'].indexOf(v) !== -1;
      },
    },
    orientation: {
      type: [Number],
      required: false,
    },
  },
  data() {
    return {
      playlistsArray: null,
      queryStructure: {
        pagePropName: 'page',
        perPagePropName: 'per_page',
        searchPropName: 'search',
        tagFilterPropName: 'rrp_pos_type',
        tierFilterPropName: 'tier',
        channelFilterPropName: 'channel',
        responseDataKey: 'playlists',
        custom: {
          status: null,
          orientation: this.orientation,
        },
      },
      fields: [
        { key: 'name', label: 'Name' },
        { key: 'orientation', label: 'Orientation' },
        { key: 'status', label: 'Status' },
        { key: 'used_in_count', label: 'Used in', tdClass: 'td-no-wrap' },
        { key: 'id', label: 'Options', tdClass: 'td-no-wrap' },
      ],
      publishStatus: ['Unpublished', 'Published'],
    };
  },
  created() {
    this.playlistsApiURL = this.playlistType;
    this.$root.$on('playlist-updated', this.playlistUpdatedExternally);
  },
  beforeDestroy() {
    this.$root.$off('playlist-updated', this.playlistUpdatedExternally);
  },
  methods: {
    itemsUpdated(data) {
      this.playlistsArray = data;
    },
    deletePlaylistProxy(playlist) {
      this.deletePlaylist(playlist).then(() => {
        this.$refs.paginationComponent.pageChanged();
      }).catch(() => { });
    },
    togglePublish(playlist) {
      const action = playlist.status === 0 ? 'publish' : 'unpublish';
      this.publishPlaylist(playlist, action).then(() => {
        this.$refs.paginationComponent.pageChanged();
      }).catch(() => { });
    },
    statusChanged() {
      this.$refs.paginationComponent.pageChanged(1);
    },
    showPlaylist(target) {
      if (this.pickerMode) {
        this.$emit('item-clicked', target);
        return;
      }
      let finalTarget = null;

      if (typeof target === 'number') {
        finalTarget = target;
      } else if (target && target.id) {
        finalTarget = target.id;
      }

      if (this.playlistType === 'playlists') {
        this.$router.push({ name: 'showPlaylist', params: { id: finalTarget } });
      } else if (this.playlistType === 'loop-playlists') {
        this.$router.push({ name: 'showLoopPlaylist', params: { id: finalTarget } });
      } else if (this.playlistType === 'weekly-playlists') {
        this.$router.push({ name: 'showWeeklyPlaylist', params: { id: finalTarget } });
      }
    },
    openPlaylist() {
      let routerName = null;
      switch (this.playlistType) {
        case 'playlists':
          routerName = 'showPlaylist';
          break;
        case 'loop-playlists':
          routerName = 'showLoopPlaylist';
          break;
        case 'weekly-playlists':
          routerName = 'showWeeklyPlaylist';
          break;
        default:
          break;
      }
      if (routerName) {
        this.playlistsArray.forEach((playlist, i) => {
          const routeData = this.$router.resolve({ name: routerName, params: { id: playlist.id } });
          setTimeout(() => {
            window.open(routeData.href, '_blank');
          }, 500 * i);
        });
      }
      return false;
    },
    openCreatePlaylist() {
      if (this.playlistType === 'playlists') {
        this.$router.push({ name: 'showPlaylist', params: { id: 'new' } });
      } else if (this.playlistType === 'loop-playlists') {
        this.$router.push({ name: 'showLoopPlaylist', params: { id: 'new' } });
      } else if (this.playlistType === 'weekly-playlists') {
        this.$router.push({ name: 'showWeeklyPlaylist', params: { id: 'new' } });
      }
    },
    playlistUpdatedExternally(updatedPlaylist) {
      if (this.playlistsArray.find(e => e.id === updatedPlaylist.id)) {
        this.$refs.paginationComponent.pageChanged();
      }
    },
    async duplicatePlaylistProxy(id) {
      try {
        await this.duplicatePlaylist(id);
        this.$refs.paginationComponent.pageChanged();
      } catch (e) { /* */ }
    },
    showPlaylistUsageModal(playlistId) {
      this.$root.$emit('modal-show-playlist-usage', {
        playlistId,
        playlistType: this.playlistType,
      })
    }
  },
  computed: {
    pageTitle() {
      return playlistViewerTitles[this.playlistType];
    },
  },
};
</script>
